import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { productsAPI } from '../utils/api';
import { Product } from '../types';
import { getEffectivePrice, isDiscounted, discountPercent } from '../utils/pricing';
import { useLanguage, TranslationKey } from '../context/LanguageContext';
import { useLocalized } from '../hooks/useLocalized';

interface SearchDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  scrolled: boolean;
}

const SearchContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.98)' : 'transparent'};
  backdrop-filter: blur(20px);
  border-bottom: ${props => props.isOpen ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
  max-height: ${props => props.isOpen ? 'calc(100vh - 70px)' : '0'};
  overflow-y: ${props => props.isOpen ? 'auto' : 'hidden'};
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  box-shadow: ${props => props.isOpen ? '0 10px 40px rgba(0, 0, 0, 0.08)' : 'none'};
  z-index: 999;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};

  @media (max-width: 768px) {
    top: 60px;
    max-height: ${props => props.isOpen ? 'calc(100vh - 60px)' : '0'};
  }
`;

const SearchInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 3rem 3rem 3rem;

  @media (max-width: 768px) {
    padding: 1.5rem 2rem 2.5rem 2rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 3rem 1rem 1rem;
  font-size: 1.1rem;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
  letter-spacing: 0.02em;
  border: none;
  border-bottom: 1px solid #280212;
  background: transparent;
  outline: none;
  transition: border-color 0.3s ease;

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  &:focus {
    border-bottom-color: #280212;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: rgba(0, 0, 0, 0.5);
  transition: color 0.3s ease;

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SearchResults = styled.div`
  max-height: 350px;
  overflow-y: auto;
  padding-right: 0.5rem;
  padding-bottom: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    padding-left: 0.5rem;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ProductImage = styled.div<{ bgImage: string }>`
  width: 60px;
  height: 60px;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  margin-right: 1.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    margin-right: 1rem;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h4`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: 400;
  color: var(--color-charcoal);
  margin-bottom: 0.25rem;
  letter-spacing: 0.02em;
`;

const ProductCategory = styled.p`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.25rem;
`;

const ProductPrice = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--color-charcoal);
  font-weight: 300;
`;

const CardOldPrice = styled.span`
  color: #b0b0b0;
  text-decoration: line-through;
  margin-right: 6px;
  font-size: 0.9em;
`;

const CardDiscountBadge = styled.span`
  color: #8a1a1a;
  font-size: 0.85em;
  margin-left: 5px;
  white-space: nowrap;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.95rem;
  font-style: italic;
`;

const SearchHint = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
`;

const ViewAllResults = styled.button`
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.2);
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #000000;
    color: white;
    border-color: var(--color-charcoal);
  }
`;

const SearchDropdown: React.FC<SearchDropdownProps> = ({ isOpen, onClose, scrolled }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { getProductName } = useLocalized();

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await productsAPI.getProducts({
          search: searchQuery,
          limit: 5
        });
        setSearchResults(response.products || []);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleProductClick = (slug: string) => {
    navigate(`/product/${slug}`);
    onClose();
  };

  const handleViewAll = () => {
    navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    onClose();
  };

  return createPortal(
    <SearchContainer isOpen={isOpen}>
      <SearchInner>
        <SearchInputWrapper>
          <SearchInput
            ref={searchInputRef}
            type="text"
            placeholder={t('header.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <CloseButton onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </SearchInputWrapper>

        {searchQuery.trim().length === 0 && (
          <SearchHint>{t('header.search.hint')}</SearchHint>
        )}

        {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
          <SearchHint>{t('header.search.minimum')}</SearchHint>
        )}

        {loading && (
          <SearchHint>{t('header.search.searching')}</SearchHint>
        )}

        {!loading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
          <NoResults>{t('header.search.noResults').replace('{query}', searchQuery)}</NoResults>
        )}

        {!loading && searchResults.length > 0 && (
          <>
            <SearchResults>
              {searchResults.map((product) => (
                <ResultItem
                  key={product._id}
                  onClick={() => handleProductClick(product.slug)}
                >
                  <ProductImage bgImage={product.images[0]?.url || ''} />
                  <ProductInfo>
                    <ProductName>{getProductName(product)}</ProductName>
                    <ProductCategory>{product.category?.name || ''}</ProductCategory>
                    <ProductPrice>
                      {isDiscounted(product) ? (
                        <>
                          <CardOldPrice>₺{product.price.toLocaleString()}</CardOldPrice>
                          ₺{getEffectivePrice(product).toLocaleString()}
                          <CardDiscountBadge>-%{discountPercent(product)}</CardDiscountBadge>
                        </>
                      ) : <>₺{product.price.toLocaleString()}</>}
                    </ProductPrice>
                  </ProductInfo>
                </ResultItem>
              ))}
            </SearchResults>
            {searchResults.length >= 5 && (
              <ViewAllResults onClick={handleViewAll}>
                {t('header.search.viewAll')}
              </ViewAllResults>
            )}
          </>
        )}
      </SearchInner>
    </SearchContainer>,
    document.body
  );
};

export default SearchDropdown;
